import { API_INSTANCE, fullBuildURL } from 'src/features/utils';
import { Attendee, AttendeeTicket, EmailActivityResponse } from 'src/types/types';

export const fetchCustomersApi = async (selectedPage: string | number) =>
  await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/customers`, `&offset=${selectedPage}`),
  );

export const fetchAttendeesByEventApi = async ({ eventId, offset = 0, limit = 10 }) =>
  await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(
      `/api/v1/dashboard/attendees/events/${eventId}`,
      `&limit=${limit}&offset=${offset}`,
    ),
  );
export const searchAttendeesByEventApi = async ({
  eventId,
  offset = 0,
  limit = 10,
  criteria,
}: {
  eventId?: string;
  offset?: number;
  limit?: number;
  criteria?: Record<string, any>;
}) => {
  // Remove 'offset' and 'limit' from the criteria object if they exist
  const { offset: _, limit: __, ...filteredCriteria } = criteria || {};

  // Convert the filtered criteria object to a query string
  const queryParams = new URLSearchParams(filteredCriteria).toString();

  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(
      `/api/v1/dashboard/attendees/events/${eventId}/search`,
      `&limit=${limit}&offset=${offset}&${queryParams}`,
    ),
  );
};

export const searchAttendeesStatsByEventApi = async ({
  eventId,
  offset = 0,
  limit = 10,
  criteria,
}: {
  eventId?: string;
  offset?: number;
  limit?: number;
  criteria?: Record<string, any>;
}) => {
  const queryParams = new URLSearchParams(criteria).toString();
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(
      `/api/v1/dashboard/attendees/events/${eventId}/stats`,
      `&limit=${limit}&offset=${offset}&${queryParams}`,
    ),
  );
};

export const fetchEventWaitlistAttendeesApi = async (eventId: string) => {
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/events/${eventId}/waitlist`),
  );
};

export const fetchAttendeesByEventApiPagination = async ({ eventId, offset = 0, limit = 20 }) => {
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(
      `/api/v1/dashboard/attendees/events/${eventId}`,
      `&limit=${limit}&offset=${offset}`,
    ),
  );
};

export const fetchWorkflowsByCustomerIdApi = async (id: string) => {
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/customers/${id}/workflows`),
  );
};
export const fetchAttendeeMonthlyTransactionsApi = async () => {
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendee-transactions/monthly`),
  );
};
export const fetchAttendeeDailyTransactionsApi = async () => {
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendee-transactions/daily`),
  );
};

export const fetchCustomerApi = async (id: string) => {
  return await API_INSTANCE.get<Attendee[]>(fullBuildURL(`/api/v1/dashboard/customers/${id}`));
};

export const fetchAttendeeApi = async (id: string) => {
  return await API_INSTANCE.get<Attendee[]>(fullBuildURL(`/api/v1/dashboard/attendees/${id}`));
};
export const fetchAttendeeByEmailApi = async () => {
  return await API_INSTANCE.get<Attendee[]>(fullBuildURL(`/api/v1/dashboard/attendees/`));
};
export const deleteAttendeeApi = async (id: string) => {
  return await API_INSTANCE.delete<Attendee[]>(fullBuildURL(`/api/v1/dashboard/attendees/${id}`));
};
export const createAttendeeApi = async (attendeeData: Record<string, unknown>) => {
  return await API_INSTANCE.post<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/`),
    attendeeData,
  );
};

export const addAttendeeNoteApi = async (data: any) => {
  return await API_INSTANCE.post<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/${data.attendee_id}/notes`),
    data,
  );
};

export const fetchAttendeeNotesApi = async (attendeeId: any) => {
  return await API_INSTANCE.get<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/${attendeeId}/notes`),
  );
};

export const updateAttendeeApi = async (attendee: any) => {
  return await API_INSTANCE.put<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/${attendee.id}`),
    attendee,
  );
};
export const patchAttendeeApi = async (attendee: any) => {
  return await API_INSTANCE.patch<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/${attendee.id}`),
    attendee,
  );
};

export const updateAttendeeTicketApi = async (attendee: any) => {
  return await API_INSTANCE.put<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/attendees/${attendee.id}/tickets`),
    attendee,
  );
};

export const cancelAndRefundAttendeeApi = async (data: {
  cart_id: string;
  reason: string;
  requestRefund: boolean;
}) => {
  return await API_INSTANCE.post<any>(
    fullBuildURL(`/api/v1/dashboard/attendee-portal/carts/${data.cart_id}/cancel-attendance`),
    {
      reason: data.reason,
      requestRefund: data.requestRefund,
    },
  );
};
export const updateCustomerApi = async (customer: any) => {
  return await API_INSTANCE.put<Attendee[]>(
    fullBuildURL(`/api/v1/dashboard/customers/${customer.id}`),
    customer,
  );
};
export const fetchOrderApi = async (checkoutId: string) => {
  return await API_INSTANCE.get<Attendee[]>(fullBuildURL(`/api/v1/dashboard/orders/${checkoutId}`));
};

export const fetchEmailActivitiesApi = async (attendeeId: Attendee['id']) => {
  return await API_INSTANCE.get<EmailActivityResponse>(
    fullBuildURL(`/api/v1/dashboard/attendee-activity/${attendeeId}/emails`),
  );
};

export const fetchAttendeeActivitiesApi = async (attendeeId: Attendee['id']) => {
  return await API_INSTANCE.get<EmailActivityResponse>(
    fullBuildURL(`/api/v1/dashboard/attendee-activity/${attendeeId}`),
  );
};

export const fetchAttendeeTicketsApi = async () => {
  return await API_INSTANCE.get<AttendeeTicket[]>(
    fullBuildURL(`/api/v1/dashboard/attendee-portal/tickets`),
  );
};

export const fetchTicketReceiptPdfApi = async (cartId: string) => {
  const url = fullBuildURL(`/api/v1/dashboard/attendee-portal/tickets/receipt-pdf/${cartId}`);
  window.open(url, '_blank');
  return { success: true };
};

export const fetchTicketPdfApi = async (cartId: string) => {
  const url = fullBuildURL(`/api/v1/dashboard/attendee-portal/tickets/pdf/${cartId}`);
  window.open(url, '_blank');
  return { success: true };
};

export const transferAttendeeTicketApi = async (data: {
  cart_id: string;
  targetEventId: string;
  reason: string;
}) => {
  return await API_INSTANCE.post<any>(
    fullBuildURL(`/api/v1/dashboard/attendee-portal/carts/${data.cart_id}/transfer-request`),
    {
      reason: data.reason,
      targetEventId: data.targetEventId,
    },
  );
};

export const fetchCartRequestsApi = async (cartId: string) => {
  return await API_INSTANCE.get<any[]>(
    fullBuildURL(`/api/v1/dashboard/attendee-portal/requests/cart/${cartId}`),
  );
};

export const fetchHostPendingRequestsApi = async () => {
  return await API_INSTANCE.get<any[]>(
    fullBuildURL(`/api/v1/dashboard/attendee-portal/host/pending-requests`),
  );
};

export const updateRequestStatusApi = async (data: {
  requestId: string;
  status: 'approved' | 'denied';
  admin_notes?: string;
  ticket_assignments?: Record<string, string>;
}) => {
  return await API_INSTANCE.put<any>(
    fullBuildURL(`/api/v1/dashboard/attendees/requests/${data.requestId}`),
    {
      status: data.status,
      admin_notes: data.admin_notes,
      ticket_assignments: data.ticket_assignments,
    },
  );
};

export const transferOrderApi = async (data: {
  orderId: string;
  target_event_id: string;
  reason?: string;
  ticket_assignments?: Record<string, string>;
  send_ticket?: boolean;
}) => {
  return await API_INSTANCE.post<any>(
    fullBuildURL(`/api/v1/dashboard/orders/${data.orderId}/transfer`),
    {
      target_event_id: data.target_event_id,
      reason: data.reason || 'Admin order transfer',
      ticket_assignments: data.ticket_assignments,
      send_ticket: data.send_ticket,
    },
  );
};
